import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Pencil, Eye, UserPlus, X, Network, Table2 } from 'lucide-react';
import { apiPersons, apiPersonManagers } from '../api/client.js';
import Avatar from '../components/Avatar';
import PeoplePicker from '../components/PeoplePicker';
import Modal from '../components/Modal.jsx';
import PersonDetailModal from '../components/PersonDetailModal';
import PersonFormModal, { MANAGER_EDITABLE_FIELDS } from '../components/PersonFormModal.jsx';
import HierarchyTable from './HierarchyTable.jsx';

const VIEWMODE_KEY = 'synolog_hierarchy_viewmode';

/**
 * Sprint 14 — Vue Hiérarchie. Deux modes (toggle persisté) :
 *  - Organigramme (chart) : arbre graphique construit à partir du snapshot
 *    `GET /persons/hierarchy/overview` (persons + liens, déjà filtré côté
 *    serveur : pas de CONTRACTOR / archivé / date de sortie dépassée).
 *    Chevron affiché uniquement si le nœud a des subordonnés visibles.
 *    « Tout déplier / replier » contrôle l'ensemble de l'arbre.
 *  - Tableau (`HierarchyTable`) : board drag&drop.
 *
 * Clic sur un nœud : ADMIN → édition complète (`PersonFormModal`) ; MANAGER →
 * édition restreinte de sa descendance (`PersonFormModal` avec
 * `editableFields = MANAGER_EDITABLE_FIELDS` : poste, avatar, notes, tags —
 * cohérent backend `PERSON_MANAGER_EDITABLE_FIELDS`). Édition des liens (ADMIN
 * seul) via « + » / « ✕ » (assign/remove, `adminRequired`).
 */

const TREE_CSS = `
.org-tree { display: inline-block; min-width: 100%; padding: 8px 0; }
.org-tree ul {
  display: flex;
  justify-content: center;
  list-style: none;
  margin: 0;
  padding: 24px 0 0;
  position: relative;
}
.org-tree li {
  list-style: none;
  margin: 0;
  padding: 24px 12px 0;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.org-tree li::before,
.org-tree li::after {
  content: '';
  position: absolute;
  top: 0;
  width: 50%;
  height: 24px;
  border-top: 2px solid var(--glass-border);
}
.org-tree li::before { right: 50%; }
.org-tree li::after { left: 50%; border-left: 2px solid var(--glass-border); }
.org-tree li:only-child::before,
.org-tree li:only-child::after { display: none; }
.org-tree li:only-child { padding-top: 24px; }
.org-tree li:first-child::before { border: 0; }
.org-tree li:last-child::after { border: 0; }
.org-tree li:last-child::before {
  border-right: 2px solid var(--glass-border);
  border-radius: 0 6px 0 0;
}
.org-tree li:first-child::after { border-radius: 6px 0 0 0; }
.org-tree ul::before {
  content: '';
  position: absolute;
  top: 0;
  left: 50%;
  width: 0;
  height: 24px;
  border-left: 2px solid var(--glass-border);
}
.org-node-wrap { display: flex; flex-direction: column; align-items: center; }
.org-node:hover .org-node-actions { opacity: 1; }
`;

const actionBtn = {
  background: 'transparent',
  border: 'none',
  color: 'var(--text-muted)',
  cursor: 'pointer',
  padding: 2,
  display: 'inline-flex',
  alignItems: 'center',
};

const cardStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 3,
  background: 'var(--card-bg)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  boxShadow: 'var(--shadow-card)',
  padding: '8px 6px 4px',
  width: 116,
  position: 'relative',
};

/**
 * Nœud d'organigramme — rendu récursif à partir des maps `childrenByManager`
 * + `personById` (pas de lazy-load). `ancestors` = garde anti-cycle (matrix
 * org : on n'affiche pas un ancêtre comme son propre descendant).
 */
function OrgNode({
  person,
  parentId,
  ancestors,
  childrenByManager,
  personById,
  expanded,
  onToggle,
  canEdit,
  canOpenEdit = canEdit,
  onOpenPerson,
  onAddChild,
  onDetach,
}) {
  const children = (childrenByManager.get(person.id) || [])
    .map((id) => personById.get(id))
    .filter(Boolean)
    .filter((c) => !ancestors.has(c.id));
  const hasChildren = children.length > 0;
  const isOpen = expanded.has(person.id);

  return (
    <div className="org-node-wrap">
      <div className="org-node" style={cardStyle}>
        {/* Actions au survol (overlay haut-droit) */}
        <div
          className="org-node-actions"
          style={{
            position: 'absolute',
            top: 2,
            right: 2,
            display: 'flex',
            gap: 1,
            opacity: 0,
            transition: 'opacity 120ms',
            background: 'var(--card-bg)',
            borderRadius: 'var(--radius-sm)',
          }}
        >
          <button type="button" onClick={() => onOpenPerson(person)} title={canOpenEdit ? 'Modifier' : 'Voir la fiche'} style={actionBtn}>
            {canOpenEdit ? <Pencil size={12} /> : <Eye size={12} />}
          </button>
          {canEdit ? (
            <button type="button" onClick={() => onAddChild(person)} title="Rattacher un subordonné" style={actionBtn}>
              <UserPlus size={12} />
            </button>
          ) : null}
          {canEdit && parentId ? (
            <button
              type="button"
              onClick={() => onDetach(person.id, parentId)}
              title="Détacher de ce manager"
              style={{ ...actionBtn, color: 'var(--danger)' }}
            >
              <X size={12} />
            </button>
          ) : null}
        </div>

        <Avatar
          person={{ firstName: person.firstName, lastName: person.lastName, avatarUrl: person.avatarUrl }}
          size={28}
        />
        <button
          type="button"
          onClick={() => onOpenPerson(person)}
          title={canOpenEdit ? 'Modifier la fiche' : 'Voir la fiche'}
          style={{ width: '100%', minWidth: 0, textAlign: 'center', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
        >
          <div style={{ fontSize: 12, fontWeight: 600, lineHeight: 1.2, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {person.lastName}
          </div>
          <div style={{ fontSize: 11, lineHeight: 1.2, color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {person.firstName}
          </div>
          {person.jobTitle ? (
            <div style={{ fontSize: 10, lineHeight: 1.2, color: 'var(--text-muted)', opacity: 0.8, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {person.jobTitle}
            </div>
          ) : null}
        </button>

        {/* Chevron — uniquement si le nœud a des subordonnés visibles. */}
        {hasChildren ? (
          <button type="button" onClick={() => onToggle(person.id)} title={isOpen ? 'Replier' : 'Déplier'} style={actionBtn}>
            {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
        ) : null}
      </div>

      {hasChildren && isOpen ? (
        <ul>
          {children.map((c) => (
            <li key={c.id}>
              <OrgNode
                person={c}
                parentId={person.id}
                ancestors={new Set([...ancestors, person.id])}
                childrenByManager={childrenByManager}
                personById={personById}
                expanded={expanded}
                onToggle={onToggle}
                canEdit={canEdit}
                canOpenEdit={canOpenEdit}
                onOpenPerson={onOpenPerson}
                onAddChild={onAddChild}
                onDetach={onDetach}
              />
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export default function HierarchyView({ user }) {
  const isAdmin = user?.role === 'ADMIN';
  const isManager = user?.role === 'MANAGER';
  const canEdit = isAdmin;
  // Un MANAGER peut éditer un sous-ensemble de champs (cf. backend
  // PERSON_MANAGER_EDITABLE_FIELDS) sur sa descendance → ouvre l'éditeur restreint.
  const canOpenEdit = isAdmin || isManager;

  const [viewMode, setViewMode] = useState(() => localStorage.getItem(VIEWMODE_KEY) || 'chart');
  const setView = (m) => {
    setViewMode(m);
    localStorage.setItem(VIEWMODE_KEY, m);
  };

  // Snapshot org (filtré serveur) pour l'organigramme.
  const [data, setData] = useState({ persons: [], edges: [] });
  const [loading, setLoading] = useState(true);
  const [rootId, setRootId] = useState(null); // ADMIN : choisi ; MANAGER : sa Person
  const [expanded, setExpanded] = useState(() => new Set());
  const [error, setError] = useState(null);

  // Modales
  const [detailPersonId, setDetailPersonId] = useState(null);
  const [editPersonId, setEditPersonId] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [addParent, setAddParent] = useState(null);

  const personById = useMemo(() => {
    const m = new Map();
    for (const p of data.persons) m.set(p.id, p);
    return m;
  }, [data.persons]);

  const childrenByManager = useMemo(() => {
    const m = new Map();
    for (const e of data.edges) {
      if (!personById.has(e.managerId) || !personById.has(e.managedId)) continue;
      if (!m.has(e.managerId)) m.set(e.managerId, []);
      m.get(e.managerId).push(e.managedId);
    }
    for (const [, ids] of m) {
      ids.sort((a, b) => {
        const pa = personById.get(a);
        const pb = personById.get(b);
        return `${pa.lastName}${pa.firstName}`.localeCompare(`${pb.lastName}${pb.firstName}`);
      });
    }
    return m;
  }, [data.edges, personById]);

  const loadOverview = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const res = await apiPersonManagers.overview();
      setData({ persons: res.persons ?? [], edges: res.edges ?? [] });
    } catch (err) {
      setError(err.message);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  // MANAGER : racine = sa propre Person (dès que le snapshot est chargé).
  useEffect(() => {
    if (isAdmin || rootId || loading) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const me = await apiPersons.me();
        if (!cancelled) {
          setRootId(me.id);
          setExpanded(new Set([me.id]));
        }
      } catch {
        /* pas de Person liée → message géré au rendu */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAdmin, rootId, loading]);

  const root = rootId ? personById.get(rootId) : null;

  // Tous les nœuds ayant au moins un enfant (pour "Tout déplier").
  const allExpandableIds = useMemo(() => new Set(childrenByManager.keys()), [childrenByManager]);

  // Picker de racine (ADMIN) — uniquement les MANAGERS (personnes ayant au
  // moins un subordonné visible), pas tout le monde.
  const fetchRootOptions = useCallback(
    async (q) => {
      const lower = (q ?? '').toLowerCase().trim();
      return data.persons
        .filter((p) => childrenByManager.has(p.id))
        .filter((p) => !lower || `${p.firstName} ${p.lastName} ${p.jobTitle ?? ''}`.toLowerCase().includes(lower))
        .slice(0, 30)
        .map((p) => ({ id: p.id, fullName: `${p.lastName} ${p.firstName}`, firstName: p.firstName, lastName: p.lastName, jobTitle: p.jobTitle, avatarUrl: p.avatarUrl }));
    },
    [data.persons, childrenByManager],
  );

  // Picker "ajouter un subordonné" — exclut le manager cible (le backend bloque
  // les cycles). Toute Person active reste rattachable.
  const fetchCandidates = useCallback(
    async (q) => {
      const { items } = await apiPersons.list('active');
      const lower = (q ?? '').toLowerCase().trim();
      return items
        .filter((p) => p.id !== addParent?.id)
        .filter((p) => !lower || `${p.firstName} ${p.lastName} ${p.email ?? ''}`.toLowerCase().includes(lower))
        .slice(0, 30)
        .map((p) => ({ id: p.id, fullName: `${p.firstName} ${p.lastName}`, firstName: p.firstName, lastName: p.lastName, jobTitle: p.jobTitle, avatarUrl: p.avatarUrl }));
    },
    [addParent],
  );

  const onToggle = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const onOpenPerson = (personOrId) => {
    const id = typeof personOrId === 'string' ? personOrId : personOrId.id;
    if (canOpenEdit) {
      setEditPersonId(id);
      setEditOpen(true);
    } else {
      setDetailPersonId(id);
    }
  };

  const onAddChild = (person) => {
    setError(null);
    setAddParent(person);
  };

  const submitAddChild = async (opt) => {
    if (!addParent) return;
    const parentId = addParent.id;
    try {
      await apiPersonManagers.assign(opt.id, parentId);
      setAddParent(null);
      setExpanded((prev) => new Set([...prev, parentId])); // déplie le parent
      await loadOverview(true);
    } catch (err) {
      setError(err.message);
    }
  };

  const onDetach = async (childId, parentId) => {
    if (!window.confirm('Détacher ce subordonné de ce manager ?')) return;
    setError(null);
    try {
      await apiPersonManagers.remove(childId, parentId);
      await loadOverview(true);
    } catch (err) {
      setError(err.message);
    }
  };

  const pill = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    background: 'transparent',
    color: 'var(--text)',
    border: '1px solid var(--glass-border)',
    borderRadius: 'var(--radius-pill)',
    padding: '6px 12px',
    fontSize: 13,
    cursor: 'pointer',
  };

  return (
    <div style={{ padding: '24px 32px' }}>
      <style>{TREE_CSS}</style>

      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginBottom: 6 }}>
          <h1 style={{ margin: 0, fontSize: 22, color: 'var(--text)' }}>Hiérarchie</h1>
          <div style={{ display: 'inline-flex', background: 'var(--glass)', border: '1px solid var(--glass-border)', borderRadius: 'var(--radius-pill)', padding: 3, gap: 2 }}>
            {[
              { key: 'chart', label: 'Organigramme', Icon: Network },
              { key: 'table', label: 'Tableau', Icon: Table2 },
            ].map(({ key, label, Icon }) => {
              const on = viewMode === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setView(key)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: on ? 'var(--accent)' : 'transparent', color: on ? '#fff' : 'var(--text)', border: 'none', borderRadius: 'var(--radius-pill)', padding: '6px 14px', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}
                >
                  <Icon size={14} /> {label}
                </button>
              );
            })}
          </div>
        </div>
        <p style={{ margin: '0 0 18px', color: 'var(--text-muted)', fontSize: 14 }}>
          {viewMode === 'table'
            ? canEdit
              ? 'Glissez un collaborateur d’un manager vers un autre pour le réaffecter.'
              : 'Vue tableau de votre équipe (lecture seule).'
            : canEdit
              ? 'Choisissez une racine, dépliez l’organigramme, cliquez sur une personne pour modifier sa fiche, « + » / « ✕ » pour rattacher/détacher. (Contractors, archivés et sortis exclus.)'
              : 'Dépliez votre équipe et cliquez sur un collaborateur pour consulter sa fiche.'}
        </p>

        {/* Barre de contrôle organigramme : racine (ADMIN) + déplier/replier. */}
        {viewMode === 'chart' ? (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
            {canEdit ? (
              <div style={{ background: 'var(--card-bg)', border: '1px solid var(--glass-border)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-card)', padding: 16, flex: '1 1 320px', maxWidth: 420 }}>
                <div style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>
                  Manager racine
                </div>
                <PeoplePicker
                  fetchOptions={fetchRootOptions}
                  onSelect={(opt) => {
                    setRootId(opt.id);
                    setExpanded(new Set([opt.id]));
                  }}
                  placeholder="Choisir un manager…"
                  emptyMessage="Aucun manager"
                />
              </div>
            ) : null}
            {root ? (
              <div style={{ display: 'inline-flex', gap: 6, alignSelf: 'center' }}>
                <button type="button" style={pill} onClick={() => setExpanded(new Set(allExpandableIds))}>
                  Tout déplier
                </button>
                <button type="button" style={pill} onClick={() => setExpanded(new Set([root.id]))}>
                  Tout replier
                </button>
              </div>
            ) : null}
          </div>
        ) : null}

        {error ? <div style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 12 }}>{error}</div> : null}
      </div>

      {/* Contenu */}
      {viewMode === 'table' ? (
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <HierarchyTable
            canEdit={canEdit}
            canOpenEdit={canOpenEdit}
            onOpenPerson={onOpenPerson}
            onChanged={() => loadOverview(true)}
          />
        </div>
      ) : loading ? (
        <div style={{ maxWidth: 1100, margin: '0 auto', color: 'var(--text-muted)' }}>Chargement…</div>
      ) : !isAdmin && !root ? (
        <div style={{ maxWidth: 1100, margin: '0 auto', color: 'var(--text-muted)' }}>
          Aucune personne n’est liée à votre compte. Impossible d’afficher votre équipe.
        </div>
      ) : root ? (
        <div style={{ overflowX: 'auto', paddingBottom: 24 }}>
          <div className="org-tree">
            <OrgNode
              person={root}
              parentId={null}
              ancestors={new Set()}
              childrenByManager={childrenByManager}
              personById={personById}
              expanded={expanded}
              onToggle={onToggle}
              canEdit={canEdit}
              canOpenEdit={canOpenEdit}
              onOpenPerson={onOpenPerson}
              onAddChild={onAddChild}
              onDetach={onDetach}
            />
          </div>
        </div>
      ) : canEdit ? (
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: 24, color: 'var(--text-muted)', textAlign: 'center', background: 'var(--card-bg)', border: '1px solid var(--glass-border)', borderRadius: 'var(--radius-md)' }}>
          Choisissez une racine ci-dessus pour afficher l’organigramme.
        </div>
      ) : null}

      {/* Modale rattacher un subordonné (ADMIN) */}
      <Modal
        open={Boolean(addParent)}
        onClose={() => setAddParent(null)}
        size="sm"
        title={addParent ? `Rattacher un subordonné à ${addParent.firstName} ${addParent.lastName}` : ''}
      >
        <div style={{ minHeight: 220 }}>
          <PeoplePicker
            fetchOptions={fetchCandidates}
            onSelect={submitAddChild}
            placeholder="Rechercher une personne…"
            autoFocus
            emptyMessage="Aucune personne disponible"
          />
        </div>
      </Modal>

      {detailPersonId ? <PersonDetailModal personId={detailPersonId} onClose={() => setDetailPersonId(null)} /> : null}

      <PersonFormModal
        open={editOpen}
        personId={editPersonId}
        editableFields={isAdmin ? null : MANAGER_EDITABLE_FIELDS}
        onClose={() => setEditOpen(false)}
        onSaved={() => loadOverview(true)}
      />
    </div>
  );
}
